import React, { ReactElement } from 'react';
import { Box } from '@mui/material';
import { Event } from 'services/api';
import { EventCardNow, EventCardNowSkeleton } from './EventCardNow';
import { FeedSectionHeader } from './FeedSectionHeader';
import { isLiveNow } from './feedEvents';
import { RailMarker } from './RailMarker';
import {
  DESKTOP_NOW_CARD,
  RAIL_COLUMN,
  RAIL_GAP,
  formatFeedClockParts,
} from './feedTokens';

const nowCardColumns = `repeat(auto-fill, minmax(min(100%, ${DESKTOP_NOW_CARD}px), ${DESKTOP_NOW_CARD}px))`;

export interface HappeningTodayProps {
  events: Event[];
  loading: boolean;
  desktop: boolean;
  hostingIds: ReadonlySet<string>;
  loadingId?: string | null;
  onOpen: (event: Event) => void;
  onCheckIn: (event: Event) => void;
}

const mobileMarker = (
  event: Event,
  index: number,
  events: Event[],
  showLine: boolean,
): ReactElement => {
  const live = isLiveNow(event);
  const previousLive = index > 0 && isLiveNow(events[index - 1]);
  if (live && index === 0)
    return <RailMarker variant="now" showLine={showLine} />;

  if (live && previousLive)
    return <RailMarker variant="continue" showLine={showLine} />;

  const clock = formatFeedClockParts(event.startTime);
  return (
    <RailMarker
      variant="time"
      time={clock?.time}
      meridiem={clock?.meridiem}
      showLine={showLine}
    />
  );
};

export const HappeningToday = ({
  events,
  loading,
  desktop,
  hostingIds,
  loadingId,
  onOpen,
  onCheckIn,
}: HappeningTodayProps): ReactElement | null => {
  if (!loading && events.length === 0) return null;

  return (
    <Box aria-label="Happening today" sx={{ width: '100%' }}>
      <FeedSectionHeader title="Happening today" />
      <Box sx={{ mt: desktop ? '16px' : '12px' }}>
        {loading ? (
          desktop ? (
            <Box sx={{ display: 'flex', gap: '16px' }}>
              <Box sx={{ width: `${RAIL_COLUMN}px` }} />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: nowCardColumns,
                  gap: '16px',
                }}
              >
                {[0, 1, 2].map(item => (
                  <EventCardNowSkeleton key={item} />
                ))}
              </Box>
            </Box>
          ) : (
            <Box>
              <EventCardNowSkeleton />
            </Box>
          )
        ) : desktop ? (
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '16px' }}>
            <RailMarker variant="today" showLine />
            <Box
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                display: 'grid',
                gridTemplateColumns: nowCardColumns,
                gap: '16px',
                alignItems: 'stretch',
              }}
            >
              {events.map((event, index) => (
                <EventCardNow
                  key={event.id}
                  event={event}
                  position={index}
                  hosting={hostingIds.has(event.id)}
                  loading={loadingId === event.id}
                  onOpen={() => onOpen(event)}
                  onCheckIn={() => onCheckIn(event)}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box>
            {events.map((event, index) => (
              <Box
                key={event.id}
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: `${RAIL_GAP}px`,
                }}
              >
                {mobileMarker(event, index, events, index < events.length - 1)}
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    pb: index < events.length - 1 ? '16px' : 0,
                  }}
                >
                  <EventCardNow
                    event={event}
                    position={index}
                    hosting={hostingIds.has(event.id)}
                    loading={loadingId === event.id}
                    onOpen={() => onOpen(event)}
                    onCheckIn={() => onCheckIn(event)}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
